import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useQueries } from '@tanstack/react-query';

import dayjs from 'shared/helpers/dayjs';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import { TopBar } from 'topBar/components/TopBar';
import {
  ShiftNoteCaptureProvider,
  useShiftNoteCapture,
} from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import { NotesStackView } from 'shiftNotes/components/NotesStack/_index';
import useIsInstantReflexSpecialist from 'shiftNotes/hooks/useIsInstantReflexSpecialist';
import NoteFormOpenContext, {
  NoteFormOpenParams,
  NoteFormOpenValue,
} from 'shiftNotes/components/NotesStack/NoteFormOpenContext';
import {
  InlineNoteCapture,
  NoteCaptureInlineProcessing,
} from 'shiftNotes/components/NoteCapture/_index';
import NoteCaptureSection from 'shiftNotes/components/NoteCapture/NoteCaptureSection';
import FormPage from 'form/pages/FormPage';
import routesConfig from 'constants/routesConfig.json';
import { APIKey, apiRoutes } from 'constants/apiKeys';
import baseFetcher from 'shared/helpers/baseFetcher';
import { Note } from 'shiftNotes/_types';

import EventSummary from 'events/components/EventSummary';
import EventVersionHistoryPage from 'events/pages/EventVersionHistoryPage';
import mapEventNoteToStackedCard from 'events/helpers/mapEventNoteToStackedCard';
import { useEvent, useInvalidateEvent } from 'events/hooks/useEvent';
import useSeedEventNotes from 'events/hooks/useSeedEventNotes';
import useEventMenuItems from 'events/hooks/useEventMenuItems';
import { usePosthogCapture } from 'events/posthogCapture/_index';
import { DashboardFilters } from 'dashboard/hooks/useDashboardFilters';
import { readStoredInputMethod } from 'settings/utils/inputMethodStorage';

import * as Styled from 'events/styles/EventPage.style';

interface EventPageProps {
  eventId: string;
  embedded?: boolean;
  autoStartRecordingForQuestionId?: string;
  onAutoStartRecordingHandled?: () => void;
  onFormOpenChange?: (isOpen: boolean) => void;
  onEventHidden?: () => void;
}

function AutoStartRecording({
  guidanceQuestionId,
  onHandled,
}: {
  guidanceQuestionId: string;
  onHandled?: () => void;
}): null {
  const { onRecordingToggle, openWritingMode, exitResolveMode } =
    useShiftNoteCapture();
  const triggeredForRef = useRef<string | null>(null);

  useEffect(() => {
    if (triggeredForRef.current === guidanceQuestionId) return;
    triggeredForRef.current = guidanceQuestionId;
    exitResolveMode();
    if (readStoredInputMethod() === 'keyboard') {
      openWritingMode(guidanceQuestionId);
    } else {
      onRecordingToggle(guidanceQuestionId);
    }
    onHandled?.();
  }, [
    guidanceQuestionId,
    onRecordingToggle,
    openWritingMode,
    exitResolveMode,
    onHandled,
  ]);

  return null;
}

export default function EventPage({
  eventId,
  embedded = false,
  autoStartRecordingForQuestionId,
  onAutoStartRecordingHandled,
  onFormOpenChange,
  onEventHidden,
}: EventPageProps): JSX.Element {
  const history = useHistory();
  const location = useLocation<{
    dashboardFilters?: DashboardFilters;
    eventsDate?: string;
    eventsSearch?: string;
    autoStartRecordingForQuestionId?: string;
  }>();
  const { t } = useTranslation('pageEvents');
  const eventsCapture = usePosthogCapture();
  const isSpecialist = useIsInstantReflexSpecialist();

  const { data: event, isLoading } = useEvent(eventId);
  const invalidateEvent = useInvalidateEvent();

  useEffect(() => {
    if (event) {
      eventsCapture.eventViewed({
        event_id: event.id,
        note_count: event.shift_notes.length,
      });
    }
  }, [event?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateBack = () => {
    if (location.state?.dashboardFilters) {
      history.replace({
        pathname: routesConfig.INSIGHTS_PAGE,
        state: { dashboardFilters: location.state.dashboardFilters },
      });
      return;
    }
    if (location.state?.eventsSearch !== undefined) {
      history.replace({
        pathname: routesConfig.EVENTS_PAGE,
        search: location.state.eventsSearch,
      });
      return;
    }
    history.replace({
      pathname: routesConfig.EVENTS_PAGE,
      search: location.state?.eventsDate
        ? `?date=${location.state.eventsDate}`
        : '',
    });
  };

  const handleBack = () => {
    eventsCapture.eventBackClicked();
    navigateBack();
  };

  useEffect(() => {
    if (event?.status !== 'false_alarm') return;
    if (embedded) {
      onEventHidden?.();
      return;
    }
    navigateBack();
  }, [event?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedNotes = useMemo(
    () =>
      [...(event?.shift_notes ?? [])].sort(
        (a, b) => dayjs(b.created).valueOf() - dayjs(a.created).valueOf()
      ),
    [event?.shift_notes]
  );

  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    setShowHistory(false);
  }, [eventId]);

  const menuItems = useEventMenuItems(event, {
    onShowHistory: () => setShowHistory(true),
  });

  useSeedEventNotes(eventId, event?.shift_notes);

  const noteQueries = useQueries({
    queries: sortedNotes.map((note) => ({
      queryKey: [APIKey.SHIFT_NOTE, note.id],
      queryFn: () => baseFetcher(apiRoutes.shiftNote(note.id)) as Promise<Note>,
      enabled: false,
    })),
  });

  const noteDataById = useMemo(() => {
    const map = new Map<string, Note>();
    noteQueries.forEach((query) => {
      const note = query.data as Note | undefined;
      if (note?.id) map.set(note.id, note);
    });
    return map;
  }, [noteQueries]);

  const stackedNotes = useMemo(
    () =>
      sortedNotes.map((note) =>
        mapEventNoteToStackedCard(noteDataById.get(note.id) ?? note, t)
      ),
    [sortedNotes, noteDataById, t]
  );

  const autoStartQuestionId =
    autoStartRecordingForQuestionId ??
    location.state?.autoStartRecordingForQuestionId;

  const handleAutoStartHandled = () => {
    onAutoStartRecordingHandled?.();
    if (location.state?.autoStartRecordingForQuestionId) {
      const nextState = { ...location.state };
      delete nextState.autoStartRecordingForQuestionId;
      history.replace({
        pathname: location.pathname,
        search: location.search,
        state: nextState,
      });
    }
  };

  const [openForm, setOpenForm] = useState<NoteFormOpenParams | null>(null);

  useEffect(() => {
    onFormOpenChange?.(openForm !== null);
  }, [openForm, onFormOpenChange]);

  const noteFormOpenValue = useMemo<NoteFormOpenValue>(
    () => ({
      onOpenForm: (params) => setOpenForm(params),
    }),
    []
  );

  const handleFormBack = useCallback(() => {
    setOpenForm(null);
  }, []);

  return (
    <ErrorBoundary>
      <ShiftNoteCaptureProvider
        eventId={eventId}
        onNotesCreated={() => invalidateEvent(eventId)}
      >
        <NoteFormOpenContext.Provider value={noteFormOpenValue}>
          {autoStartQuestionId && (
            <AutoStartRecording
              guidanceQuestionId={autoStartQuestionId}
              onHandled={handleAutoStartHandled}
            />
          )}
          {showHistory && event ? (
            <EventVersionHistoryPage
              event={event}
              onBack={() => setShowHistory(false)}
            />
          ) : (
            <Styled.EventPageWrapper>
              {openForm ? (
                <FormPage
                  initialFormInstance={openForm.formInstance}
                  initialIsCompleted={openForm.isCompleted}
                  origin={openForm.origin}
                  onBack={handleFormBack}
                  embedded
                />
              ) : (
                <>
                  {!embedded && (
                    <TopBar
                      onBackClick={handleBack}
                      menuItems={menuItems}
                      showBackButton
                      showOnDesktop
                      backButtonAriaLabel={t('eventPage.backToInsights')}
                    />
                  )}
                  {isLoading && (
                    <Styled.EventTopSection>
                      <Styled.LoadingWrapper>
                        <CircularProgress />
                      </Styled.LoadingWrapper>
                    </Styled.EventTopSection>
                  )}
                  {!isLoading && event && (
                    <>
                      <Styled.EventTopSection>
                        <Styled.EventTopSectionInner>
                          <EventSummary event={event} />
                        </Styled.EventTopSectionInner>
                      </Styled.EventTopSection>
                      <Styled.EventBottomSection>
                        <Styled.EventBottomSectionInner>
                          <InlineNoteCapture
                            eventId={eventId}
                            canResolve={event.status === 'open'}
                          />
                          <NoteCaptureSection
                            mobileView={false}
                            suppressReady
                          />
                          <NoteCaptureInlineProcessing />
                          <NotesStackView
                            notes={stackedNotes}
                            isSpecialist={isSpecialist}
                            canUnsyncFromEvent={stackedNotes.length > 1}
                            eventId={eventId}
                          />
                        </Styled.EventBottomSectionInner>
                      </Styled.EventBottomSection>
                    </>
                  )}
                </>
              )}
            </Styled.EventPageWrapper>
          )}
        </NoteFormOpenContext.Provider>
      </ShiftNoteCaptureProvider>
    </ErrorBoundary>
  );
}
